import { EventEmitter } from '../../core/Event';
import type { Random } from '../../core/Random';
import type { HigherLowerEvent } from './HigherLowerEvents';
import type {
  Direction,
  HigherLowerConfig,
  HigherLowerStatus,
} from './HigherLowerTypes';

/**
 * Authoritative state for a Higher/Lower session — current card, streak,
 * multiplier, balance. Hosts call {@link startRound} to deal the first
 * card, {@link predict} for each guess, and {@link cashOut} to lock in
 * winnings before risking the streak again.
 */
export class HigherLowerModel {
  public readonly events = new EventEmitter<HigherLowerEvent>();
  public readonly bet: number;
  public readonly multiplierByStreak: readonly number[];
  public readonly cardMin: number;
  public readonly cardMax: number;
  private readonly random: Random;
  private _balance: number;
  private _status: HigherLowerStatus = 'idle';
  private _currentCard = 1;
  private _streak = 0;

  constructor(config: HigherLowerConfig, random: Random) {
    this._balance = config.startingBalance;
    this.bet = config.bet;
    this.multiplierByStreak = config.multiplierByStreak;
    this.cardMin = config.cardMin ?? 1;
    this.cardMax = config.cardMax ?? 13;
    if (this.cardMax <= this.cardMin) {
      throw new Error(`cardMax (${this.cardMax}) must be > cardMin (${this.cardMin})`);
    }
    if (this.multiplierByStreak.length < 2) {
      throw new Error('multiplierByStreak needs at least 2 entries (streak 0 + 1)');
    }
    this.random = random;
  }

  get balance(): number {
    return this._balance;
  }

  get status(): HigherLowerStatus {
    return this._status;
  }

  get currentCard(): number {
    return this._currentCard;
  }

  get streak(): number {
    return this._streak;
  }

  get multiplier(): number {
    const idx = Math.min(this._streak, this.multiplierByStreak.length - 1);
    return this.multiplierByStreak[idx]!;
  }

  /** Start a new round — debit the bet, deal the first card, await the
   * player's first prediction. Returns false if the player can't afford
   * the bet or a round is already in flight. */
  startRound(): boolean {
    if (this._status === 'awaiting-prediction') return false;
    if (this._balance < this.bet) {
      this.events.emit({
        kind: 'not-enough-coins',
        required: this.bet,
        have: this._balance,
      });
      return false;
    }
    const balBefore = this._balance;
    this._balance -= this.bet;
    this.events.emit({
      kind: 'balance-changed',
      before: balBefore,
      after: this._balance,
      reason: 'round-bet',
    });
    this._currentCard = this.drawCard();
    this._streak = 0;
    this._status = 'awaiting-prediction';
    this.events.emit({
      kind: 'round-started',
      startingCard: this._currentCard,
      bet: this.bet,
    });
    return true;
  }

  /** Resolve a prediction. Draws the next card, compares, updates streak.
   * Wrong guess (or tie) ends the round with status='lost'. Returns the
   * new card. No-op (returns null) when not awaiting a prediction. */
  predict(direction: Direction): number | null {
    if (this._status !== 'awaiting-prediction') return null;
    const fromCard = this._currentCard;
    const next = this.drawCard();
    const correct =
      (direction === 'higher' && next > fromCard) ||
      (direction === 'lower' && next < fromCard);

    if (correct) {
      this._streak += 1;
      this._currentCard = next;
      this.events.emit({
        kind: 'prediction-resolved',
        direction,
        fromCard,
        toCard: next,
        correct: true,
        streakAfter: this._streak,
        multiplierAfter: this.multiplier,
      });
    } else {
      this._status = 'lost';
      this.events.emit({
        kind: 'prediction-resolved',
        direction,
        fromCard,
        toCard: next,
        correct: false,
        streakAfter: 0,
        multiplierAfter: this.multiplierByStreak[0]!,
      });
    }
    return next;
  }

  /** Lock in the current multiplier and credit {@code bet × multiplier}.
   * Returns false unless awaiting a prediction with at least one correct
   * guess banked (cashing out at streak 0 just refunds the bet, which
   * we still allow for player-friendliness). */
  cashOut(): boolean {
    if (this._status !== 'awaiting-prediction') return false;
    const m = this.multiplier;
    const winnings = Math.floor(this.bet * m);
    if (winnings > 0) {
      const balBefore = this._balance;
      this._balance += winnings;
      this.events.emit({
        kind: 'balance-changed',
        before: balBefore,
        after: this._balance,
        reason: 'cash-out',
      });
    }
    this._status = 'cashed-out';
    this.events.emit({
      kind: 'cashed-out',
      multiplier: m,
      winnings,
      streak: this._streak,
    });
    return true;
  }

  dispose(): void {
    this.events.clear();
  }

  private drawCard(): number {
    return this.random.nextIntBetween(this.cardMin, this.cardMax + 1);
  }
}
